import { GeneralConfig } from '../config/generalConfig';
import { useAuthStore } from '../stores/authStore';
import { useCompanyStore } from '../stores/companyStore';
import { useCountryStore } from '../stores/countryStore';
import { useCurrencyStore } from '../stores/currencyStore';
import { useDateFormatStore } from '../stores/dateFormatStore';
import { useDemoInfoStore } from '../stores/demoInfoStore';
import { useDemoStoreStore } from '../stores/demoStoreStore';
import { useLanguageStore } from '../stores/languageStore';
import { useMasterDemoStore } from '../stores/masterDemoStore';
import { useModuleStore } from '../stores/moduleStore';
import { useStoreListStore } from '../stores/storeListStore';
import { useStoreSettingStore } from '../stores/storeSettingStore';
import { Company } from '../types/Company';
import { Country } from '../types/Country';
import { Currency } from '../types/Currency';
import { DemoInfo } from '../types/DemoInfo';
import { Module } from '../types/Module';
import { Storefront } from '../types/Storefront';
import { StoreSetting } from '../types/StoreSetting';
import { User } from '../types/User';
import { getAuthToken, setModuleData, setStoreAuthToken } from './storage';

export const configureSystem = (response: any): void => {
  const dataMap = response as Record<string, any>;
  const data = dataMap.loaded_modules as Record<string, any> | undefined;

  if (!data) {
    return;
  }

  const { setCompany } = useCompanyStore.getState();
  const { loadModule } = useModuleStore.getState();
  const { setLanguage } = useLanguageStore.getState();
  const { setCurrency } = useCurrencyStore.getState();
  const { setFormat: setDateFormat } = useDateFormatStore.getState();
  const { setCountry } = useCountryStore.getState();
  const { authenticate, unauthenticate } = useAuthStore.getState();
  const authToken = getAuthToken();
  const { setStoreSetting, updateDefaultStore } =
    useStoreSettingStore.getState();
  const { setStoreList } = useStoreListStore.getState();
  const { setMasterDemoStatus } = useMasterDemoStore.getState();
  const { setDemoInfo } = useDemoInfoStore.getState();
  const { setDemoStoreId } = useDemoStoreStore.getState();

  const company = Company.fromJson(data.company as Record<string, any>);
  setCompany(company);

  const module = Module.fromJson(data.modules as Record<string, any>);
  loadModule(module);
  setModuleData(module);

  const language = data.default_language as Record<string, any>;
  const languageCode = language.code as string;
  setLanguage(languageCode);

  const currency = Currency.fromJson(
    data.default_currency as Record<string, any>
  );
  setCurrency(currency);

  const dateFormat = data.date_format as string;
  setDateFormat(dateFormat);

  const country = Country.fromJson(data.default_country as Record<string, any>);
  setCountry(country);

  const userMap = data.user as Record<string, any> | undefined;
  if (userMap) {
    const user = User.fromJson(userMap);
    authenticate(authToken!, user);
  } else {
    unauthenticate();
  }

  if (module.store) {
    const storeSetting = StoreSetting.fromJson(
      data.store_settings as Record<string, any>
    );
    setStoreSetting(storeSetting);

    const stores = (data.stores as Array<any>).map((item) =>
      Storefront.fromJson(item as Record<string, any>)
    );
    setStoreList(stores);

    const storeToken = data.customer_token as string | null;
    if (storeToken) {
      setStoreAuthToken(storeToken);
    }
  }

  const demoStatus = GeneralConfig.isDemo && (data.is_demo as boolean);
  if (demoStatus) {
    const masterDemoStatus = data.is_master_demo as boolean;
    setMasterDemoStatus(masterDemoStatus);

    const demoInfo = DemoInfo.fromJson(data.demo_info as Record<string, any>);
    setDemoInfo(demoInfo);

    if (module.store) {
      const defaultStoreId = data.default_store_id as number;
      setDemoStoreId(defaultStoreId);
      updateDefaultStore(defaultStoreId);
    }
  }
};
